/**
 * Hook for managing authentication session
 * Handles session validation on app startup and automatic token refresh
 */

import { useEffect, useCallback, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { validateSession, refreshToken } from '@/store/thunks/authThunks'
import { setLoading } from '@/store/slices/authSlice'
import AuthService from '@/services/authService'

export const useAuthSession = () => {
  const dispatch = useAppDispatch()
  const { isAuthenticated, user, isLoading, token } = useAppSelector((state) => state.auth)
  const [isInitialized, setIsInitialized] = useState(false)

  // Validate session on app startup
  const initializeAuth = useCallback(async () => {
    // Only run initialization once
    if (isInitialized) return

    // If we have a stored token but no user, validate the session
    if (token && !user) {
      dispatch(setLoading(true))
      dispatch(validateSession())
    }
    
    setIsInitialized(true)
  }, [token, user, isInitialized, dispatch])

  // Handle automatic token refresh
  const handleTokenRefresh = useCallback(async () => {
    if (!isAuthenticated || !token) return

    try {
      const refreshResult = await AuthService.handleTokenRefresh()
      if (refreshResult) {
        // Token was refreshed successfully
        dispatch(refreshToken())
      }
    } catch (error) {
      console.warn('Automatic token refresh failed:', error)
    }
  }, [isAuthenticated, token, dispatch])

  // Initialize authentication on mount
  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  // Set up token refresh interval (check every 5 minutes)
  useEffect(() => {
    if (!isAuthenticated) return

    const interval = setInterval(handleTokenRefresh, 5 * 60 * 1000) // 5 minutes

    return () => clearInterval(interval)
  }, [isAuthenticated, handleTokenRefresh])

  // Check for token refresh on window focus (in case user was away for a while)
  useEffect(() => {
    if (!isAuthenticated) return

    const handleFocus = () => {
      handleTokenRefresh()
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [isAuthenticated, handleTokenRefresh])

  return {
    isAuthenticated,
    user,
    isLoading: isLoading && isInitialized, // Only show loading after initialization attempt
    isSessionInitialized: isInitialized,
    initializeAuth,
  }
}

export default useAuthSession 